import { useContext, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiContext } from '@shared/contexts';
import { usePollingJob } from '@shared/hooks';
import { EBulkOperationStatus } from '@modules/items/types/bulkOperation.types';

import { EAiRunFeedbackSentiment, type TAiRunFeedbackMutationArgs } from '../types';
import { AI_EDIT_POLL_INTERVAL_MS } from './constants';
import type { TAiEditDecision, TAiEditRequest, TAiEditRunResponse } from './types';

type TSubmitAiEditDecisionsInput = {
  runId: string;
  decisions: TAiEditDecision[];
};

const useRequestAiEdit = () => {
  const { apiClient, queryClient } = useContext(ApiContext);
  const [terminalRun, setTerminalRun] = useState<TAiEditRunResponse>();
  const polling = usePollingJob<TAiEditRunResponse>({
    queryKeyPrefix: 'ai-edit-run',
    endpoint: '/ai/edit',
    pollingInterval: AI_EDIT_POLL_INTERVAL_MS,
    showToast: false,
    onCompleted: setTerminalRun,
    onFailed: setTerminalRun,
  });
  const mutation = useMutation<TAiEditRunResponse, Error, TAiEditRequest>({
    mutationFn: async (request) => {
      const response = await apiClient.post<TAiEditRunResponse>('/ai/edit', request);
      return response.data;
    },
  });

  const mutateAsync = async (request: TAiEditRequest): Promise<TAiEditRunResponse> => {
    setTerminalRun(undefined);
    polling.setJobId(null);
    mutation.reset();
    return mutation.mutateAsync(request);
  };

  // Separate from creating: only the attempt that still owns the run may start polling it,
  // so a cancelled attempt's late create response cannot steal the newer attempt's watch.
  const watch = (run: TAiEditRunResponse) => {
    if (run.status !== EBulkOperationStatus.CREATED) return;
    queryClient.setQueryData([`ai-edit-run-${run.runId}`], () => run);
    polling.setJobId(run.runId);
  };

  const reset = () => {
    setTerminalRun(undefined);
    polling.setJobId(null);
    mutation.reset();
  };

  return {
    mutateAsync,
    watch,
    reset,
    run: terminalRun ?? polling.data ?? mutation.data,
    isPending: mutation.isPending || polling.isPolling,
    pollingError: polling.error,
  };
};

export default useRequestAiEdit;

export const useSubmitAiEditDecisions = () => {
  const { apiClient } = useContext(ApiContext);
  return useMutation<void, Error, TSubmitAiEditDecisionsInput>({
    mutationFn: async ({ runId, decisions }) => {
      await apiClient.put(`/ai/edit/${runId}/decisions`, { decisions });
    },
  });
};

export const useCancelAiEditReview = () => {
  const { apiClient } = useContext(ApiContext);
  return useMutation<void, Error, string>({
    mutationFn: async (runId) => {
      await apiClient.put(`/ai/edit/${runId}/cancel`);
    },
  });
};

export const useCancelAiEditRun = () => {
  const { apiClient } = useContext(ApiContext);
  return useMutation<void, Error, string>({
    mutationFn: async (runId) => {
      await apiClient.put(`/ai/edit/${runId}/cancel-run`);
    },
  });
};

export const useSubmitAiEditFeedback = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);

  return useMutation<void, Error, TAiRunFeedbackMutationArgs>({
    mutationFn: async ({ runId, feedback }) => {
      await apiClient.put(`/ai/edit/${runId}/feedback`, feedback);
    },
    onSuccess: (_, { feedback }) => {
      const message =
        feedback.sentiment === EAiRunFeedbackSentiment.POSITIVE ? 'Positive feedback sent.' : 'Feedback sent.';
      show('success', message);
    },
    onError: handleApiError('Sending feedback failed!'),
  });
};
